/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/hooks/useConfigurationQuery.ts
 * @Description: 运营配置查询 Hook
 *
 * 使用 React Query 在客户端获取运营配置数据
 * 用于功能开关、业务规则等配置
 */

import { useQuery } from '@tanstack/react-query';
import { getConfiguration } from 'api/user';
import { queryKeys } from 'libs/react-query';

/**
 * 使用 React Query 获取运营配置数据
 *
 * @param options - 查询选项
 * @param options.enabled - 是否启用查询（默认为 true）
 * @param options.initialData - 初始数据（可选，用于服务端渲染场景）
 *
 * @returns 返回查询结果对象，包含：
 *   - data: 运营配置数据数组
 *   - isLoading: 是否正在首次加载
 *   - isFetching: 是否正在获取数据（包括后台更新）
 *   - isError: 是否发生错误
 *   - error: 错误信息
 *   - refetch: 手动重新获取数据的函数
 *
 * @example
 * ```tsx
 * function SomeFeature() {
 *   const { data: configuration, isLoading } = useConfigurationQuery();
 *
 *   const enableFeature = configuration?.find(item => item.key === 'enableNewFeature')?.value;
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return enableFeature ? <NewFeature /> : <OldFeature />;
 * }
 * ```
 */
export function useConfigurationQuery(options?: { enabled?: boolean; initialData?: any[] }) {
  const { enabled = true, initialData } = options || {};

  return useQuery({
    // 查询键，用于缓存和标识查询
    queryKey: queryKeys.configuration.list(),

    // 查询函数，返回 Promise
    queryFn: async () => {
      try {
        const res = await getConfiguration({
          query: { _t: Date.now() },
        });

        if (res.code === 200) {
          return res.data || [];
        }

        throw new Error(res.message || '获取运营配置失败');
      } catch (error) {
        console.error('获取运营配置数据失败:', error);
        throw error;
      }
    },

    // 是否启用查询
    enabled,

    // 初始数据（用于服务端渲染场景）
    initialData,

    // 数据在5分钟内被认为是新鲜的
    staleTime: 2 * 60 * 1000,
  });
}
